import { useState } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import classNames from 'classnames';

import { CarouselTexts, verticalDoctorCardTexts } from '@constant/home';
import { Button } from '@com/_atoms/NewButton';
import Icon from '@utilities/icon';
import { colors } from '@configs/Theme';
import VerticalDoctorCard from '@com/shared/cards/doctor/vertical-doctor-card';
import VerticalDoctorShimmer from '@com/shared/cards/doctor/vertical-doctor-shimmer';
import NobatDoctorCard from '../../../pages/app/nobat/components/doctor-card';
import NobatDoctorCardShimmer from '../../../pages/app/nobat/components/doctor-card/shimmer';
import { routeList } from '@routes/routeList';
import { useGetHomeCarouselDetails } from '@api/home/index.rq';
import useInView from '@hooks/useInView';

const ScrollSlider = dynamic(() => import('@com/shared/scroll-slider'));

type HomeDoctorsCarouselProps = {
  groupKey: string;
  title: string;
  backgroundColor?: string;
  headerBackgroundColor?: string;
  moreLinkTitle?: string;
  moreLink: string;
  carouselCardClassName?: string;
  containerClassName?: string;
  scrollSliderClassName?: string;
  isShowMoreButton?: boolean;
  isInNobatPage?: boolean;
  inPersonCard?: boolean;
  hasDoctorsCount?: boolean;
};

const shimerData = [...Array(10).keys()];

const HomeDoctorsCarousel = ({
  groupKey,
  title,
  backgroundColor,
  headerBackgroundColor,
  moreLink,
  carouselCardClassName,
  containerClassName,
  scrollSliderClassName,
  isShowMoreButton = true,
  isInNobatPage,
  inPersonCard,
  hasDoctorsCount,
}: HomeDoctorsCarouselProps) => {
  const { ref, isInView } = useInView();
  const { push } = useRouter();
  const { data, isLoading } = useGetHomeCarouselDetails(
    { groupKey: groupKey },
    {
      enabled: isInView,
    },
  );

  const [routeLoadingId, setRouteLoadingId] = useState<number | null>(null);

  const onClickHandler = (doctorId: number) => {
    if (!doctorId) return;
    setRouteLoadingId(doctorId);
    push(
      `${routeList?.doctorProfile}/${doctorId}?${isInNobatPage ? 'fromNobat=true' : 'fromHome=true'}`,
    );
  };

  return (
    <div
      ref={ref}
      className={classNames(
        'mt-3 flex flex-col select-none',
        isInNobatPage ? 'h-[306px]' : inPersonCard ? 'h-[260px]' : 'h-[240px]',
        containerClassName,
      )}
      style={
        backgroundColor
          ? { backgroundColor: backgroundColor ? backgroundColor : '' }
          : {}
      }
    >
      <div
        className={classNames(
          'h-[48px] flex items-center justify-between px-4',
        )}
        style={
          headerBackgroundColor
            ? {
                backgroundColor: headerBackgroundColor && headerBackgroundColor,
              }
            : {}
        }
      >
        <div className="flex items-center ">
          <span
            className={classNames('text-Label/Medium text-content-primary')}
          >
            {title}{' '}
            {hasDoctorsCount && data?.pages[0]?.totalCount > 0 && (
              <span className="text-Body/Small text-content-tertiary">
                ( {data?.pages[0]?.totalCount} پزشک )
              </span>
            )}
          </span>
        </div>

        {isShowMoreButton && (
          <Button
            variant="secondary"
            size="medium"
            className="h-10 w-10 rounded-full !p-0"
            aria-label="More"
            onClick={() =>
              push(
                `${moreLink}?groupKey=${groupKey}&${isInNobatPage ? 'fromNobat=true' : 'fromHome=true'}&pageNumber=1&pageSize=20`,
              )
            }
            icon={
              <Icon
                name="ChevronLeft"
                width={1.5}
                height={1.5}
                fill={colors.black}
              />
            }
          />
        )}
      </div>

      <ScrollSlider
        className={classNames(
          !isShowMoreButton && 'pl-4',
          'flex h-full items-center gap-x-2 px-4',
          scrollSliderClassName,
        )}
      >
        <>
          {isLoading ? (
            shimerData.map((item) =>
              isInNobatPage ? (
                <NobatDoctorCardShimmer key={item} />
              ) : (
                <VerticalDoctorShimmer key={item} />
              ),
            )
          ) : (
            <>
              {data?.pages?.[0]?.queryResult?.map((item) => {
                const profileUrl = `${routeList?.doctorProfile}/${item?.doctorId}?${isInNobatPage ? 'fromNobat=true' : 'fromHome=true'}`;
                const isCardLoading =
                  !!routeLoadingId && item?.recId === routeLoadingId;

                if (isInNobatPage) {
                  return (
                    <NobatDoctorCard
                      key={item?.doctorId}
                      onClick={() => onClickHandler(item?.doctorId)}
                      appointmentCount={item?.inPersonBookings}
                      isLoading={isCardLoading}
                      className={carouselCardClassName}
                      imageUrl={item?.imageUrl}
                      star={item?.rate}
                      cities={item?.cities ?? []}
                      title={item?.title}
                      subtitle={item?.subtitle}
                      buttonText={verticalDoctorCardTexts.inPersonBooking}
                    />
                  );
                }

                if (inPersonCard) {
                  return (
                    <VerticalDoctorCard
                      key={item?.doctorId}
                      onClick={() => {
                        setRouteLoadingId(item?.recId);
                        push(profileUrl);
                      }}
                      isLoading={isCardLoading}
                      className={carouselCardClassName}
                      imageUrl={item?.imageUrl}
                      star={item?.rate}
                      cities={item?.cities ?? []}
                      title={item?.title}
                      subtitle={item?.subtitle}
                      isActive={false}
                      reserveCitiesRow
                      buttonText={verticalDoctorCardTexts.inPersonBooking}
                    />
                  );
                }

                return (
                  <VerticalDoctorCard
                    key={item?.doctorId}
                    onClick={() => {
                      setRouteLoadingId(item?.recId);
                      push(profileUrl);
                    }}
                    isLoading={isCardLoading}
                    className={carouselCardClassName}
                    imageUrl={item?.imageUrl}
                    star={item?.rate}
                    cities={item?.cities ?? []}
                    title={item?.title}
                    subtitle={item?.subtitle}
                    activity={item?.activityStatus}
                    isOndemand
                    isScheduled
                  />
                );
              })}
            </>
          )}
        </>

        {isShowMoreButton && (
          <div
            onClick={() =>
              push(
                `${moreLink}?groupKey=${groupKey}&${isInNobatPage ? 'fromNobat=true' : 'fromHome=true'}&pageNumber=1&pageSize=20`,
              )
            }
            className={classNames(
              'flex flex-col items-center justify-center cursor-pointer text-content-primary text-Label/xSmall bg-surface-primary rounded-lg shadow-sm',
              isInNobatPage
                ? 'min-w-[172px] min-h-[268px]'
                : 'min-w-[152px] h-[204px]',
            )}
            onKeyDown={() => {
              return;
            }}
            tabIndex={0}
            role="button"
          >
            <Icon
              name="ArrowLeft"
              width={1.24}
              height={1.24}
              fill={colors?.black}
            />

            {CarouselTexts?.viewAll}
          </div>
        )}
      </ScrollSlider>
    </div>
  );
};
export default HomeDoctorsCarousel;
